import { ApolloError, gql, useMutation } from '@apollo/client';
import { useToast } from '@quality24/design-system';
import {
  Alert,
  EnumServiceCustomFieldInputType,
  EnumServicesCustomFieldWhen,
  JsonServiceActionModalData,
  JsonServiceCustomFieldData,
  OrderCustomFieldValue,
  PaymentConfig,
  Service,
  ServiceCustomField,
  UUIDv4,
} from '@quality24/inpatient-typings';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import {
  ChargeableItemFragment,
  OrderChargeableItemFragment,
  QueryServiceCustomField,
} from '../../@types/global';
import useI18nResolvers from '../../i18n/useI18nResolvers';
import useTriggerOrderStore from './store';

// GraphQL
// //////////////////////////

const CREATE_ORDER_WITH_CUSTOM_FIELDS_MUTATION = gql`
  mutation CreateOrderWithCustomFieldsMutation(
    $alert: AlertInput!
    $orderCustomFields: [OrderCustomFieldValueInput]
  ) {
    createOrderWithCustomFields(
      input: { alert: $alert, orderCustomFields: $orderCustomFields }
    ) {
      clientMutationId
    }
  }
`;

const CREATE_ORDER_WITH_PAYMENT_MUTATION = gql`
  mutation CreateOrderWithPaymentMutation(
    $input: AlertInput!
    $orderChargeableItems: [OrderChargeableItemInput]
    $orderCustomFields: [OrderCustomFieldValueInput]
  ) {
    createOrderWithPayment(
      input: {
        alert: $input
        orderChargeableItems: $orderChargeableItems
        orderCustomFields: $orderCustomFields
      }
    ) {
      clientMutationId
    }
  }
`;

// Type defs
// //////////////////////////

export type PatientCallReasonFragment = Required<
  Pick<
    Service,
    | 'id'
    | 'reason'
    | 'reasonI18N'
    | 'modifier'
    | 'customizationType'
    | 'customizationData'
    | 'actions'
    | 'icons'
    | 'conditional'
    | 'serviceType'
  >
>;

export interface ComposedPatientCallReason extends PatientCallReasonFragment {
  groupId?: UUIDv4;
  lastAlert?: Required<Pick<Alert, 'createdAt'>> | null;
  activeAlert?: Pick<Alert, 'id'>;
  hasChildren?: boolean;
  children?: Array<ComposedPatientCallReason>;
  chargeableItems?: Array<ChargeableItemFragment>;
  paymentConfig?: Pick<PaymentConfig, 'id' | 'data'> | null;
  serviceCustomFields?: Array<{
    id: string;
    serviceId: string;
    order: number;
    label: string;
    labelI18N: string;
    inputType: string;
    placeholder: string | null;
    placeholderI18N: string;
    required: boolean;
    data: unknown;
    when: string;
  }>;
}

export interface HookResult {
  currentStep:
    | 'idle'
    | 'select-child'
    | 'select-chargeable-items'
    | 'check-coverage'
    | 'custom-fields-form'
    | 'select-custom-fields'
    | 'select-customization'
    | 'trigger'
    | 'trigger-with-payment';
  reason?: ComposedPatientCallReason;
  serviceCustomFields: QueryServiceCustomField[];
  dispatch: React.Dispatch<HookAction>;
  operation: {
    data?: { clientMutationId?: string };
    error?: ApolloError;
    loading: boolean;
    called: boolean;
    submitting: boolean;
  };
}

export interface HookState {
  step:
    | 'idle'
    | 'select-child'
    | 'select-chargeable-items'
    | 'check-coverage'
    | 'custom-fields-form'
    | 'select-custom-fields'
    | 'select-customization'
    | 'trigger'
    | 'trigger-with-payment';
  alert: Pick<Alert, 'id' | 'admissionId'> & {
    reasonId?: UUIDv4;
    groupId?: UUIDv4;
    customization?: { value: string };
    details?: Record<string, unknown>;
    serviceType?: string;
  };
  reason?: (ComposedPatientCallReason | PatientCallReasonFragment) & {
    groupId?: UUIDv4;
  };
  orderChargeableItems?: OrderChargeableItemFragment[];
  customFields?: Array<
    Pick<
      OrderCustomFieldValue,
      | 'alertId'
      | 'fieldId'
      | 'order'
      | 'label'
      | 'inputType'
      | 'required'
      | 'value'
      | 'data'
    >
  >;
}

// type: 'init' | 'select-child' | 'select-customization' | 'reset';
export type HookAction =
  | {
      type: 'init';
      reason: ComposedPatientCallReason;
    }
  | {
      type: 'select-child';
      reason: ComposedPatientCallReason;
    }
  | {
      type: 'select-chargeable-items';
      orderId: UUIDv4;
      customization: { value: string };
      orderChargeableItems: OrderChargeableItemFragment[];
    }
  | {
      type: 'check-coverage';
      covered: boolean;
    }
  | {
      type: 'select-custom-fields';
      customFields: Array<
        Pick<
          OrderCustomFieldValue,
          | 'alertId'
          | 'fieldId'
          | 'order'
          | 'label'
          | 'inputType'
          | 'required'
          | 'value'
          | 'data'
        >
      >;
    }
  | {
      type: 'select-customization';
      customization: { value: string };
    }
  | {
      type: 'reset';
      admissionId?: UUIDv4;
    };

// Helper functions
// //////////////////////////

/**
 * Formats custom field values for mutation
 * @param alertId UUID of the alert
 * @param serviceCustomFields Array of service custom fields
 * @param values Form values from CustomFieldsForm
 * @returns Formatted array of OrderCustomFieldValue
 */
export const formatOrderCustomFieldValues = (
  alertId: UUIDv4,
  serviceCustomFields: Array<
    Pick<
      ServiceCustomField,
      'id' | 'order' | 'label' | 'inputType' | 'required' | 'data'
    >
  >,
  values: Record<string, string>,
): Array<
  Pick<
    OrderCustomFieldValue,
    | 'alertId'
    | 'fieldId'
    | 'order'
    | 'label'
    | 'inputType'
    | 'required'
    | 'value'
    | 'data'
  >
> => {
  return serviceCustomFields.map((field) => ({
    alertId,
    fieldId: field.id,
    order: field.order,
    label: field.label,
    inputType: field.inputType,
    required: field.required,
    value:
      field.inputType === 'DATE'
        ? moment(values[field.id], 'YYYY-MM-DD').format('DD/MM/YYYY')
        : values[field.id] || '-',
    data: field.data,
  }));
};

// Hooks definition
// //////////////////////////

/**
 * Initial trigger flow state
 */
const getInitialState = (admissionId?: UUIDv4): HookState => ({
  step: 'idle',
  alert: {
    id: uuid() as UUIDv4,
    admissionId: admissionId || (uuid() as UUIDv4),
  },
  reason: undefined,
});

/**
 *
 * @param state
 * @param action
 * @returns
 */
export const triggerFlowReducer: React.Reducer<HookState, HookAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case 'init': {
      const result = {
        ...state,
        reason: action.reason,
      };
      // if has child, move to select-child
      if (action.reason.hasChildren) {
        return { ...result, step: 'select-child' };
      }
      // if has customization, move to select-customization
      if (action.reason.customizationType !== 'NONE') {
        return {
          ...result,
          alert: {
            ...state.alert,
            reasonId: action.reason.id,
            groupId: state.reason?.groupId,
          },
          step: 'select-customization',
        };
      }

      return {
        ...result,
        alert: {
          ...state.alert,
          reasonId: action.reason.id,
          groupId: action.reason.groupId,
        },
        step: 'custom-fields-form',
      };
    }

    case 'select-child': {
      const result = {
        ...state,
        alert: {
          ...state.alert,
          reasonId: action.reason.id,
          groupId: action.reason.groupId,
        },
        reason: action.reason,
      };
      // if has children, move to select-child
      if (action.reason.hasChildren) {
        return { ...state, reason: action.reason, step: 'select-child' };
      }
      // if has multiple chargeable items, move to select-chargeable-items
      if (
        action.reason.chargeableItems &&
        action.reason.chargeableItems.length > 1
      ) {
        return { ...result, step: 'select-chargeable-items' };
      }
      // if has a single chargeable items, move to chargeable-items
      if (
        action.reason.chargeableItems &&
        action.reason.chargeableItems.length === 1
      ) {
        return {
          ...result,
          orderChargeableItems: [
            {
              orderId: uuid() as UUIDv4,
              itemId: action.reason.chargeableItems[0].id,
              name: action.reason.chargeableItems[0]?.name,
              description: action.reason.chargeableItems[0]?.description,
              shortDescription:
                action.reason.chargeableItems[0]?.shortDescription,
              sequentialOrder: 0,
              price: action.reason.chargeableItems[0]?.price,
              quantity: 1,
            },
          ],
          step: 'check-coverage',
        };
      }
      // if has customization, move to select-customization
      if (action.reason.customizationType !== 'NONE') {
        return { ...result, step: 'select-customization' };
      }
      return { ...result, step: 'custom-fields-form' };
    }

    case 'select-chargeable-items': {
      return {
        ...state,
        alert: {
          ...state.alert,
          id: action.orderId,
          groupId: state.reason?.groupId,
          customization: action.customization,
        },
        orderChargeableItems: action.orderChargeableItems,
        step: 'check-coverage',
      };
    }

    case 'check-coverage': {
      return {
        ...state,
        step: action.covered ? 'custom-fields-form' : 'trigger-with-payment',
      };
    }

    case 'select-customization': {
      return {
        ...state,
        alert: {
          ...state.alert,
          customization: action.customization,
          groupId: state.reason?.groupId,
        },
        step: 'custom-fields-form',
      };
    }

    case 'select-custom-fields': {
      const result = {
        ...state,
        customFields: action.customFields,
        alert: {
          ...state.alert,
          reasonId: state.reason?.id || state.alert.reasonId,
          groupId: state.reason?.groupId,
        },
      };
      // Update alertId in customFields to match state.alert.id
      const customFieldsWithCorrectAlertId = action.customFields.map(
        (field) => ({
          ...field,
          alertId: state.alert.id,
        }),
      );
      return {
        ...result,
        customFields: customFieldsWithCorrectAlertId,
        step: 'trigger',
      };
    }

    case 'reset':
      return getInitialState(action.admissionId);

    default:
      throw new Error('Unexpected action type');
  }
};

/**
 * Handles alert trigger flow.
 * - If reason-group has children, display selector for sub-reasons-group
 * - If reason-group has customization, display selector for customization value
 * - Else trigger alert
 * @param admissionId
 * @returns
 */
const useTriggerOrder = (admissionId?: UUIDv4): HookResult => {
  const [state, dispatch] = React.useReducer(
    triggerFlowReducer,
    getInitialState(admissionId),
  );
  const { toast } = useToast();
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const { setLoading, loading: submitting } = useTriggerOrderStore();

  // Process service custom fields from reason
  const serviceCustomFields = React.useMemo<QueryServiceCustomField[]>(() => {
    const reason = state.reason as ComposedPatientCallReason | undefined;
    if (
      !reason?.serviceCustomFields ||
      reason.serviceCustomFields.length === 0
    ) {
      return [];
    }
    return reason.serviceCustomFields
      .filter((field) => field)
      .sort((a, b) => a.order - b.order)
      .map((field) => ({
        ...field,
        id: field.id as UUIDv4,
        serviceId: field.serviceId as UUIDv4,
        inputType: field.inputType as EnumServiceCustomFieldInputType,
        when: field.when as EnumServicesCustomFieldWhen,
        data: field.data as JsonServiceCustomFieldData & {
          refCustomFieldId?: UUIDv4;
        },
      }));
  }, [state.reason]);

  // Auto-transition from custom-fields-form to trigger if no custom fields
  React.useEffect(() => {
    if (
      state.step === 'custom-fields-form' &&
      serviceCustomFields.length === 0
    ) {
      // No custom fields, go directly to trigger by dispatching empty custom fields
      dispatch({ type: 'select-custom-fields', customFields: [] });
    }
  }, [state.step, serviceCustomFields.length, dispatch]);

  /**
   * Performs every 'on-trigger-local' actions
   * @param reason the patient call reason
   */
  const performTriggerActions = React.useCallback(
    (reason: PatientCallReasonFragment): void => {
      if (!reason.actions || reason.actions.length === 0) return;
      reason.actions
        .filter((action) => action.hookType === 'on-trigger-local')
        .forEach((action) => {
          // For now we are only handling the modal actions
          if (action.action !== 'modal') return;
          const data = action.data as JsonServiceActionModalData['data'];
          const content = resolveI18n(data.contentI18N) || data.content;
          if (!content) return;
          toast(content);
        });
    },
    [toast, resolveI18n],
  );

  // Prepare mutation
  const [
    triggerOrderWithCustomFields,
    {
      data: dataWithCustomFields,
      error: errorWithCustomFields,
      loading: loadingWithCustomFields,
      called: calledWithCustomFields,
    },
  ] = useMutation(CREATE_ORDER_WITH_CUSTOM_FIELDS_MUTATION, {
    onCompleted: () => dispatch({ type: 'reset', admissionId }),
  });

  const [
    triggerOrderWithPayment,
    {
      data: dataWithPayment,
      error: errorWithPayment,
      loading: loadingWithPayment,
      called: calledWithPayment,
    },
  ] = useMutation(CREATE_ORDER_WITH_PAYMENT_MUTATION, {
    onCompleted: () => dispatch({ type: 'reset', admissionId }),
  });

  const operation: HookResult['operation'] = React.useMemo(() => {
    if (state.step === 'check-coverage') {
      return {
        data: dataWithPayment,
        error: errorWithPayment,
        loading: loadingWithPayment,
        called: calledWithPayment,
        submitting,
      };
    }

    return {
      data: dataWithCustomFields,
      error: errorWithCustomFields,
      loading: loadingWithCustomFields,
      called: calledWithCustomFields,
      submitting,
    };
  }, [
    calledWithCustomFields,
    calledWithPayment,
    dataWithCustomFields,
    dataWithPayment,
    errorWithCustomFields,
    errorWithPayment,
    loadingWithCustomFields,
    loadingWithPayment,
    state.step,
    submitting,
  ]);

  // Track if mutation has been called to prevent infinite loops
  const triggerMutationCalledRef = React.useRef(false);
  const triggerWithPaymentMutationCalledRef = React.useRef(false);

  // Perform mutation if state === 'trigger'
  React.useEffect(() => {
    if (
      state.step === 'trigger' &&
      !triggerMutationCalledRef.current &&
      !loadingWithCustomFields
    ) {
      triggerMutationCalledRef.current = true;
      setLoading(true);
      triggerOrderWithCustomFields({
        variables: {
          alert: {
            ...state.alert,
            // Ensure details is always provided (required field)
            details: state.alert.details || {},
            // Ensure serviceType is always provided (required field)
            // Default to 'admission' if not available from reason
            serviceType:
              (state.reason as PatientCallReasonFragment)?.serviceType ||
              'admission',
          },
          orderCustomFields: state.customFields,
        },
      })
        .then(() => {
          if (state.reason) {
            performTriggerActions(state.reason);
          }
        })
        .catch(() => {
          toast(t('errors.requestFailed'));
          // Reset the flow on error. Keeping the step at 'trigger' (and only
          // resetting the ref) would re-run this effect and re-fire the
          // mutation in an infinite loop.
          dispatch({ type: 'reset', admissionId });
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // Reset ref when step changes away from trigger
    if (state.step !== 'trigger') {
      triggerMutationCalledRef.current = false;
    }
  }, [
    admissionId,
    performTriggerActions,
    setLoading,
    state.step,
    state.alert,
    state.reason,
    state.customFields,
    loadingWithCustomFields,
    toast,
    t,
    triggerOrderWithCustomFields,
  ]);

  // Perform mutation if state === 'trigger-with-payment'
  React.useEffect(() => {
    if (
      state.step === 'trigger-with-payment' &&
      !triggerWithPaymentMutationCalledRef.current &&
      !loadingWithPayment
    ) {
      triggerWithPaymentMutationCalledRef.current = true;
      setLoading(true);
      triggerOrderWithPayment({
        variables: {
          input: {
            ...state.alert,
            // Paid orders should be created as `pending`
            status: 'PENDING',
            // Ensure serviceType is always provided (required field)
            serviceType:
              (state.reason as PatientCallReasonFragment)?.serviceType ||
              'admission',
          },
          orderChargeableItems: state.orderChargeableItems,
          orderCustomFields: state.customFields,
        },
      })
        .then(() => {
          if (state.reason) {
            performTriggerActions(state.reason);
          }
        })
        .catch(() => {
          toast(t('errors.requestFailed'));
          // Reset the flow on error. Keeping the step at 'trigger-with-payment'
          // (and only resetting the ref) would re-run this effect and re-fire
          // the mutation in an infinite loop.
          dispatch({ type: 'reset', admissionId });
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // Reset ref when step changes away from trigger-with-payment
    if (state.step !== 'trigger-with-payment') {
      triggerWithPaymentMutationCalledRef.current = false;
    }
  }, [
    admissionId,
    performTriggerActions,
    setLoading,
    state.step,
    state.alert,
    state.reason,
    state.orderChargeableItems,
    state.customFields,
    loadingWithPayment,
    toast,
    t,
    triggerOrderWithPayment,
  ]);

  return {
    currentStep: state.step,
    reason: state.reason,
    serviceCustomFields,
    dispatch,
    operation,
  };
};

export default useTriggerOrder;
